import dayjs from 'dayjs';

export const DEFAULT_SILENCE_DURATION = '3h';

const DURATION_TOKEN_REGEX = /(\d+(?:\.\d+)?)\s*(months?|mo|weeks?|w|days?|d|hours?|hrs?|hr|h|minutes?|mins?|min|m)(?![a-z])/gi;

const UNIT_ALIASES = {
    month: 'month',
    months: 'month',
    mo: 'month',
    week: 'week',
    weeks: 'week',
    w: 'week',
    day: 'day',
    days: 'day',
    d: 'day',
    hour: 'hour',
    hours: 'hour',
    hr: 'hour',
    hrs: 'hour',
    h: 'hour',
    minute: 'minute',
    minutes: 'minute',
    min: 'minute',
    mins: 'minute',
    m: 'minute',
};

function invalidDuration(error = 'Use values like 30m, 2h, 1 day, 1 month') {
    return {
        isValid: false,
        isPermanent: false,
        endSilence: null,
        error,
    };
}

export function parseSilenceDuration(input, now = dayjs()) {
    const rawValue = typeof input === 'string'
        ? input.trim()
        : String(input ?? '').trim();

    if (!rawValue) {
        return {
            isValid: true,
            isPermanent: true,
            endSilence: null,
            error: '',
        };
    }

    const normalizedValue = rawValue
        .toLowerCase()
        .replace(/,/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    if (normalizedValue === 'permanent' || normalizedValue === 'forever') {
        return {
            isValid: true,
            isPermanent: true,
            endSilence: null,
            error: '',
        };
    }

    if (/^\d+$/.test(normalizedValue)) {
        const minutes = Number(normalizedValue);
        if (minutes <= 0) {
            return invalidDuration('Duration must be greater than zero');
        }

        return {
            isValid: true,
            isPermanent: false,
            endSilence: now.add(minutes, 'minute').valueOf(),
            error: '',
        };
    }

    DURATION_TOKEN_REGEX.lastIndex = 0;
    const parts = [];
    let lastIndex = 0;
    let match = DURATION_TOKEN_REGEX.exec(normalizedValue);

    while (match) {
        const separator = normalizedValue.slice(lastIndex, match.index);
        if (!/^\s*$/.test(separator)) {
            return invalidDuration();
        }

        const amount = Number(match[1]);
        const unit = UNIT_ALIASES[match[2]];
        if (!unit || amount <= 0) {
            return invalidDuration('Duration must be greater than zero');
        }

        parts.push({ amount, unit });
        lastIndex = DURATION_TOKEN_REGEX.lastIndex;
        match = DURATION_TOKEN_REGEX.exec(normalizedValue);
    }

    if (parts.length === 0 || !/^\s*$/.test(normalizedValue.slice(lastIndex))) {
        return invalidDuration();
    }

    let endSilence = dayjs(now);
    parts.forEach(({ amount, unit }) => {
        endSilence = endSilence.add(amount, unit);
    });

    return {
        isValid: true,
        isPermanent: false,
        endSilence: endSilence.valueOf(),
        error: '',
    };
}

export function formatSilenceDuration(endSilence, now = Date.now()) {
    if (endSilence === null || endSilence === undefined) {
        return '';
    }

    const endTimestamp = Number(endSilence);
    if (Number.isNaN(endTimestamp)) {
        return '';
    }

    let totalMinutes = Math.max(0, Math.ceil((endTimestamp - Number(now)) / 60000));
    if (totalMinutes <= 0) {
        return '';
    }

    const days = Math.floor(totalMinutes / (24 * 60));
    totalMinutes -= days * 24 * 60;

    const hours = Math.floor(totalMinutes / 60);
    totalMinutes -= hours * 60;

    const minutes = totalMinutes;
    const parts = [];

    if (days > 0) {
        parts.push(`${days}d`);
    }
    if (hours > 0) {
        parts.push(`${hours}h`);
    }
    if (minutes > 0) {
        parts.push(`${minutes}m`);
    }

    return parts.join(' ');
}

export function formatSilenceEnd(endSilence) {
    if (endSilence === null || endSilence === undefined) {
        return 'permanent';
    }

    const timestamp = Number(endSilence);
    if (Number.isNaN(timestamp)) {
        return '';
    }

    return dayjs(timestamp).format('DD MMM YYYY HH:mm');
}
